import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Receipt, Package, TrendingUp, Users, Clock, DollarSign } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Card } from '../components/common/Card';
import { theme, media } from '../styles/theme';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.background};
  animation: fadeIn 0.5s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  padding-bottom: 100px;
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    padding-bottom: 80px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: ${theme.spacing.sm};
    padding-bottom: 80px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing['2xl']};
  
  h1 {
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing.sm};
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accentTeal} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.lg};
    margin: 0;
  }

  ${media.down.sm} {
    margin-bottom: ${theme.spacing.xl};
    
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
    
    p {
      font-size: ${theme.typography.fontSize.base};
    }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const SummaryCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.xl};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  border: 1px solid ${theme.colors.border};
  border-left: 4px solid ${({ color }) => color || theme.colors.primary};
  background: ${theme.colors.white};
  cursor: pointer;
  box-shadow: ${theme.shadows.sm};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, ${({ color }) => `${color || theme.colors.primary}10`} 0%, transparent 70%);
    opacity: 0;
    transition: opacity ${theme.transitions.duration.base};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
    border-left-width: 5px;
    
    &::before {
      opacity: 1;
    }
  }

  ${media.down.sm} {
    padding: ${theme.spacing.lg};
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color }) => `${color || theme.colors.primary}10`};
  color: ${({ color }) => color || theme.colors.primary};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }

  ${SummaryCard}:hover & {
    background: ${({ color }) => `${color || theme.colors.primary}15`};
  }

  ${media.down.sm} {
    width: 48px;
    height: 48px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const SummaryContent = styled.div`
  flex: 1;
  
  h3 {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.xs};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  ${media.down.sm} {
    h3 {
      font-size: ${theme.typography.fontSize.xs};
    }
    
    p {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const RupeesIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: inherit;
  font-family: 'Arial', sans-serif;
`;

const ChartCard = styled(Card)`
  padding: ${theme.spacing.xl};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.sm};
  
  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
    padding-bottom: ${theme.spacing.md};
    border-bottom: 1px solid ${theme.colors.border};
  }

  &:hover {
    box-shadow: ${theme.shadows.md};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.lg};
    
    h2 {
      font-size: ${theme.typography.fontSize.lg};
    }
  }
`;

// Mock data
const salesData = [
  { name: 'Mon', sales: 12000 },
  { name: 'Tue', sales: 15000 },
  { name: 'Wed', sales: 18000 },
  { name: 'Thu', sales: 14000 },
  { name: 'Fri', sales: 22000 },
  { name: 'Sat', sales: 25000 },
  { name: 'Sun', sales: 20000 },
];

const stockData = [
  { name: 'Rice', usage: 45, stock: 55 },
  { name: 'Flour', usage: 30, stock: 70 },
  { name: 'Oil', usage: 25, stock: 75 },
  { name: 'Spices', usage: 40, stock: 60 },
  { name: 'Vegetables', usage: 50, stock: 50 },
];

function AdminDashboard() {
  const navigate = useNavigate();

  return (
    <DashboardContainer>
      <Sidebar />
      <MainContent>
        <Header user={{ name: 'Admin User', role: 'Administrator' }} />
        <ContentWrapper>
          <PageHeader>
            <h1>Dashboard Overview</h1>
            <p>Welcome back! Here's what's happening at your restaurant today.</p>
          </PageHeader>

        <SummaryGrid>
          <SummaryCard 
            color={theme.colors.primary}
            onClick={() => navigate('/admin/sales')}
          >
            <IconWrapper color={theme.colors.primary}>
              <RupeesIcon>₹</RupeesIcon>
            </IconWrapper>
            <SummaryContent>
              <h3>Today's Sales</h3>
              <p>₹25,450</p>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard 
            color={theme.colors.status.preparing}
            onClick={() => navigate('/admin/orders')}
          >
            <IconWrapper color={theme.colors.status.preparing}>
              <Receipt />
            </IconWrapper>
            <SummaryContent>
              <h3>Active Orders</h3>
              <p>12</p>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard 
            color={theme.colors.status.pending}
            onClick={() => navigate('/admin/inventory')}
          >
            <IconWrapper color={theme.colors.status.pending}>
              <Package />
            </IconWrapper>
            <SummaryContent>
              <h3>Low Stock Items</h3>
              <p>5</p>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard color={theme.colors.accentTeal}>
            <IconWrapper color={theme.colors.accentTeal}>
              <TrendingUp />
            </IconWrapper>
            <SummaryContent>
              <h3>Growth Rate</h3>
              <p>+12.5%</p>
            </SummaryContent>
          </SummaryCard>
        </SummaryGrid>

        <ChartsGrid>
          <ChartCard>
            <h2>Sales Overview</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke={theme.colors.primary}
                  strokeWidth={2}
                  name="Sales (₹)"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard>
            <h2>Stock Usage</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stockData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="usage" fill={theme.colors.accent} name="Usage %" />
                <Bar dataKey="stock" fill={theme.colors.primary} name="Stock %" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsGrid>
        </ContentWrapper>
        <Footer />
      </MainContent>
    </DashboardContainer>
  );
}

export default AdminDashboard;

